import { useQueryClient } from '@tanstack/react-query';
import { FormikHelpers } from 'formik';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useMutationModalContext } from '../../contexts/MutationModalProvider';
import useClientActions from '../../hooks/APIHooks/users/useClientActions';
import { Client } from '../../types/users';
import { fetchErrorMessage } from '../../utils/fetchErrorMessage';
import ChangePasswordModal from '../CommonComps/Modals/ChangePasswordModal';
import ConsentModal from '../CommonComps/Modals/ConsentModal';
import EditClientModal from '../CommonComps/Modals/EditClientModal';

const ClientMutationModals = () => {
  // integration of react hooks here
  const [isDeactivating, setIsDeactivating] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // integration of context hooks here
  const {
    data,
    showUpdateModal,
    setShowUpdateModal,
    showPasswordModal,
    setShowPasswordModal,
    showDeactivateModal,
    setShowDeactivateModal,
    showDeleteModal,
    setShowDeleteModal,
  } = useMutationModalContext();

  // integration of custom hooks here
  const {
    changeClientPasswordHandler,
    deleteClientByIdHandler,
    toggleClientActivityHandler,
  } = useClientActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // handler function to delete client declared here
  const deleteClientHandler = async () => {
    try {
      setIsDeleting(true);

      await deleteClientByIdHandler
        .mutateAsync({ clientId: (data as Client).id })
        .then((res) => {
          if (res.status === 204) {
            toast.success('Client Deleted!!');

            queryClient.invalidateQueries({ queryKey: ['client-list'] });
            setShowDeleteModal(false);
          } else {
            toast.error(res.data.message || 'Client Deleting Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      setIsDeleting(false);
    }
  };

  // handler function to change client's password declared here
  const changePasswordHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      console.log(values, data);

      await changeClientPasswordHandler
        .mutateAsync({
          clientId: (data as Client).id,
          body: { password: values.new_password },
        })
        .then((res) => {
          console.log('CHANGE PASS RES: ', res);
          if (res.is_success) {
            toast.success('Password Updated!!');

            queryClient.invalidateQueries({ queryKey: ['client-list'] });
            setShowPasswordModal(false);
          } else {
            toast.error(res.message || 'Password Update Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
    } finally {
      actions.setSubmitting(false);
    }
  };

  // handler function to deactivate associate declared here
  const deactivateClientHandler = async () => {
    try {
      setIsDeactivating(true);

      await toggleClientActivityHandler
        .mutateAsync({
          clientId: (data as Client).id,
          body: { is_active: !(data as Client).is_active },
        })
        .then((res) => {
          console.log('DEACTIVATE RES: ', res);
          if (res.is_success) {
            toast.success(
              `Client ${res.data.is_active ? 'Activated' : 'Deactivated'}!!`
            );

            queryClient.invalidateQueries({ queryKey: ['client-list'] });
            setShowDeactivateModal(false);
          } else {
            toast.error(
              res.message ||
                `${res.data.is_active ? 'Activation' : 'Deactivation'} Failed!!`
            );
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
    } finally {
      setIsDeactivating(false);
    }
  };

  // rendering client mutation modals section component here
  return (
    <section>
      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showUpdateModal && (
          <EditClientModal
            client={data as Client}
            handleModalClose={() => setShowUpdateModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showPasswordModal && (
          <ChangePasswordModal
            changePasswordFn={changePasswordHandler}
            handleModalClose={() => setShowPasswordModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showDeactivateModal && (
          <ConsentModal
            titleText='Are you sure?'
            subTitle={
              <>
                Are you sure you want to{' '}
                {(data as Client).is_active ? 'deactivate' : 'activate'} this
                client?
              </>
            }
            isLoading={isDeactivating}
            yesBtnTxt='Yes'
            yesBtnVariant='danger'
            noBtnTxt='No'
            noBtnVariant='success'
            mutationHandler={deactivateClientHandler}
            handleModalClose={() => setShowDeactivateModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showDeleteModal && (
          <ConsentModal
            titleText='Are you sure?'
            subTitle={
              <>
                Are you sure you want to delete this client? Once you delete it,
                you can not recover it.
              </>
            }
            isLoading={isDeleting}
            yesBtnTxt='Yes'
            yesBtnVariant='danger'
            noBtnTxt='No'
            noBtnVariant='success'
            mutationHandler={deleteClientHandler}
            handleModalClose={() => setShowDeleteModal(false)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default ClientMutationModals;
